import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { industryTypes, yearsInBusinessOptions } from '../data/mock';

const QuoteRequestModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    businessName: '',
    industry: '',
    yearsInBusiness: '',
    contactName: '',
    phone: '',
    email: '',
    equipmentNeeded: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Mock submission
    console.log('Quote Request Submitted:', formData);
    
    toast.success('Quote request received!', {
      description: 'Our team will contact you within 2 hours during business hours.'
    });
    
    // Reset form
    setFormData({
      businessName: '',
      industry: '',
      yearsInBusiness: '',
      contactName: '',
      phone: '',
      email: '',
      equipmentNeeded: '',
      message: ''
    });
    
    onClose();
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-gray-900">
            Request Your Quote
          </DialogTitle>
          <DialogDescription className="text-gray-600">
            Fill out the form below and we'll get back to you with a detailed quote within 2 hours during business hours.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Business Information */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900">Business Information</h3>
            
            <div>
              <Label htmlFor="businessName">Business Name *</Label>
              <Input
                id="businessName"
                value={formData.businessName}
                onChange={(e) => handleChange('businessName', e.target.value)}
                placeholder="Your Company Name"
                required
              />
            </div>

            <div>
              <Label htmlFor="industry">Type of Industry *</Label>
              <Select 
                value={formData.industry} 
                onValueChange={(value) => handleChange('industry', value)}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select your industry" />
                </SelectTrigger>
                <SelectContent>
                  {industryTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="yearsInBusiness">How Long in Business? *</Label>
              <Select 
                value={formData.yearsInBusiness} 
                onValueChange={(value) => handleChange('yearsInBusiness', value)}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select years in business" />
                </SelectTrigger>
                <SelectContent>
                  {yearsInBusinessOptions.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Contact Information */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900">Contact Information</h3>
            
            <div>
              <Label htmlFor="contactName">Contact Name *</Label>
              <Input
                id="contactName"
                value={formData.contactName}
                onChange={(e) => handleChange('contactName', e.target.value)}
                placeholder="John Doe"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="(555) 123-4567"
                  required
                />
              </div>

              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="john@company.com"
                  required
                />
              </div>
            </div>
          </div>

          {/* Project Details */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900">Project Details</h3>
            
            <div>
              <Label htmlFor="equipmentNeeded">Equipment Needed</Label>
              <Input
                id="equipmentNeeded"
                value={formData.equipmentNeeded}
                onChange={(e) => handleChange('equipmentNeeded', e.target.value)}
                placeholder="e.g., 32' Scissor Lift, Forklift"
              />
            </div>

            <div>
              <Label htmlFor="message">Additional Details</Label>
              <Textarea
                id="message"
                value={formData.message}
                onChange={(e) => handleChange('message', e.target.value)}
                placeholder="Tell us about your project, rental duration, location, etc."
                rows={4}
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex gap-4">
            <Button
              type="submit"
              className="flex-1 bg-orange-600 hover:bg-orange-700 text-white py-6 text-lg font-semibold"
            >
              Submit Quote Request
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="px-8"
            >
              Cancel
            </Button>
          </div>

          <p className="text-xs text-gray-500 text-center">
            By submitting this form, you agree to be contacted by Cooper Equipment Rentals regarding your quote request.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteRequestModal;
